import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, CONTACT_INFO } from '../data/mock';
import { Link } from 'react-router-dom';
import { Eye, Award, Users, Shield, ArrowRight, Sparkles, Heart } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Clarity Lens Co | Premium Eyewear';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[280px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.aboutBanner}
            alt="About Clarity Lens Co"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <Eye className="w-3.5 h-3.5" />
                  Our Story
                </span>
                <h1 className="font-display text-4xl sm:text-5xl text-white font-semibold tracking-tight mb-4">
                  About Us
                </h1>
                <p className="text-white/80 text-base">
                  Premium eyewear for those who value clarity and style
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-wider mb-3 block">
              Our Mission
            </span>
            <h2 className="font-display text-3xl text-neutral-900 font-semibold tracking-tight mb-6">
              Precision Vision, Timeless Style
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed">
              At Clarity Lens Co, we believe everyone deserves to see clearly and look exceptional doing it. We're dedicated to providing high-quality, stylish eyewear that protects your eyes while enhancing your personal style. From blue light blocking glasses to premium sunglasses, we curate only the finest for our customers.
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-wider mb-3 block">
                Our Values
              </span>
              <h2 className="font-display text-3xl text-neutral-900 font-semibold tracking-tight">
                What We Stand For
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Eye, title: 'Quality First', desc: 'Every pair of eyewear we sell meets rigorous quality standards for durability, comfort, and protection.' },
                { icon: Heart, title: 'Customer Focus', desc: "Your satisfaction is our priority. We're here to help you find the perfect eyewear for your lifestyle." },
                { icon: Sparkles, title: 'Style Matters', desc: 'We believe eyewear should be both functional and fashionable. Express yourself with our curated collection.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 text-center hover:shadow-card transition-all">
                  <div className="w-14 h-14 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-5">
                    <item.icon className="w-7 h-7 text-neutral-700" />
                  </div>
                  <h3 className="font-medium text-neutral-900 text-lg mb-3">{item.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-neutral-400 text-sm font-medium uppercase tracking-wider mb-3 block">
                  Why Choose Us
                </span>
                <h2 className="font-display text-3xl text-neutral-900 font-semibold tracking-tight mb-8">
                  The Clarity Lens Difference
                </h2>
                <div className="space-y-5">
                  {[
                    { icon: Shield, title: 'Premium Protection', desc: 'All our eyewear features UV400 protection and quality lens technology.' },
                    { icon: Award, title: '30-Day Guarantee', desc: 'Not satisfied? Return within 30 days for a full refund, no questions asked.' },
                    { icon: Users, title: 'Expert Support', desc: 'Our team is here to help you find the perfect frames for your needs.' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-neutral-700" />
                      </div>
                      <div>
                        <h4 className="font-medium text-neutral-900 mb-1">{item.title}</h4>
                        <p className="text-neutral-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img 
                  src={siteImages.featuredBackground}
                  alt="Premium eyewear"
                  className="rounded-2xl w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-neutral-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl text-white font-semibold tracking-tight mb-4">
              Ready to Find Your Perfect Pair?
            </h2>
            <p className="text-neutral-400 mb-10">
              Browse our collection and discover eyewear that matches your style.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-neutral-100 text-neutral-900 font-medium rounded-full transition-colors"
              >
                Shop Now
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 text-white font-medium hover:text-neutral-300 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
